import { Layout, PURPLE_700, PURPLE_800, RED_700 } from '@mnd-frontend/ui';
import { localizeValue, scaledRemCalc } from '@mnd-frontend/utils';
import type { ScaleBand, ScaleLinear } from 'd3-scale';
import React, { memo, useLayoutEffect } from 'react';
import styled, { css } from 'styled-components';
import { PositionRelative, SvgChart } from '../lib/components';
import { Axes, Grid } from '../lib/components/Graph';
import { ContextProvider as _ContextProvider, useSvgContext } from '../lib/context';
import { useScaleBandY, useScaleLinearX } from '../lib/hooks/d3';

const ContextProvider = _ContextProvider as any;

const StyledText = styled.text`
  fill: ${PURPLE_700};
  cursor: pointer;
  font-weight: normal;

  ${({ onClick }) =>
    Boolean(onClick) &&
    css`
      &:hover {
        fill: ${PURPLE_800};
      }
    `}
`;

interface DataPoint {
  name: string;
  displayValue?: string;
  value: number;
}
interface PropsBarChart {
  data: DataPoint[];
  onClick?: (value: DataPoint) => void;
}
interface PropsRectsHorizontal extends PropsBarChart {
  scaleX: ScaleLinear<number, number, never>;
  scaleY: ScaleBand<string>;
}

const formatValue = (value: number) => localizeValue(value, { abbrev: true });

interface PropsTextComponent {
  data: DataPoint;
  onClick?: (value: DataPoint) => void;
  x: number;
  y: number;
  isFirst: boolean;
}

const TextComponent = ({ x, y, onClick, data, isFirst }: PropsTextComponent) => {
  const { updatePadding }: any = useSvgContext();
  const ref = React.createRef<SVGTextElement>();
  useLayoutEffect(() => {
    if (isFirst) updatePadding({ right: ref.current?.getBoundingClientRect().width });
  }, [ref, updatePadding, isFirst]);

  return (
    <StyledText
      ref={ref}
      x={x}
      y={y}
      dominantBaseline="central"
      fontWeight="bold"
      fontSize={scaledRemCalc(14)}
      onClick={() => onClick && onClick(data)}
    >
      {data.displayValue ?? (data.value === null ? '-' : formatValue(data.value))}
    </StyledText>
  );
};

const RectsHorizontal = memo(({ data, scaleX, scaleY, onClick }: PropsRectsHorizontal) => {
  const height = scaleY.bandwidth();

  return (
    <>
      {data.map((d, i) => {
        const x0 = scaleX(0);
        const width = scaleX(d.value) - x0;
        const y = scaleY(i.toString()) || 0;
        return (
          <React.Fragment key={`${d.name}-${i}`}>
            <rect y={y} x={x0} width={width} height={height} fill={RED_700} />
            <TextComponent
              isFirst={i === 0}
              data={d}
              x={x0 + width + 4}
              y={y + height * 0.5}
              onClick={onClick}
            />
          </React.Fragment>
        );
      })}
    </>
  );
});

const Bars = memo(({ data, onClick }: PropsBarChart) => {
  const dataWithMaxValue = [...data].sort((a, b) => b.value - a.value)[0];
  const scaleX = useScaleLinearX(0, dataWithMaxValue.value);
  const scaleY = useScaleBandY(data, 0.4, 0.2);

  return (
    <PositionRelative>
      <SvgChart>
        <Grid.Horizontal scaleX={scaleX} scaleY={scaleY} />
        <RectsHorizontal data={data} scaleX={scaleX} scaleY={scaleY} onClick={onClick} />
        <Axes.Horizontal data={data} scaleX={scaleX} scaleY={scaleY} />
      </SvgChart>
    </PositionRelative>
  );
});

const BarChart = ({ data, onClick }: PropsBarChart) => (
  <ContextProvider minWidth={200} height={data.length * 32} selectToggle hideSelected>
    <Layout gap={2} $flexDirection="column">
      <Bars data={data} onClick={onClick} />
    </Layout>
  </ContextProvider>
);

export default memo(BarChart);
